import { getPageExportGenerate } from 'services/analyticsExports';
import { extractFilenameFromContentDisposition } from 'utils/tableCsvExport';
import { ExportChartView } from './chartSpecs';
import { ExportChartCaptureResult } from './captureOrchestrator';
import { BuildPreflightPayloadArgs, buildPreflightPayload } from './preflight';
import { parseRetryAfter } from './http';

export type GenerateFailureReason =
  | 'file_size_exceeded'
  | 'page_limit_exceeded'
  | 'upstream_analytics_failed'
  | 'permission_denied'
  | 'png_invalid'
  | 'timeout'
  | 'throttled'
  | 'no_data'
  | 'request_failed'
  | 'unexpected_response_shape';

export type GenerateResult =
  | { ok: true; blob: Blob; filename: string }
  | { ok: false; reason: GenerateFailureReason; retryAfter?: number };

// Maps a captured chart's view id to the multipart field key the backend
// requires (must match CHART_FILENAME_STEMS exactly).
const CHART_FIELD_BY_VIEW: Record<ExportChartView, string> = {
  users: 'users_with_activity',
  activities: 'overall_activities',
  videos: 'video_started_finished',
  videos_overall: 'video_time_watched',
  quizzes: 'quizzes',
  labs: 'hands_on_labs',
};

export function buildGenerateFormData(
  args: BuildPreflightPayloadArgs,
  charts: ExportChartCaptureResult[]
): FormData {
  const payload = buildPreflightPayload(args);
  const formData = new FormData();
  formData.append('format', payload.format);
  formData.append('account_id', payload.account_id);
  formData.append('export_types', JSON.stringify(payload.export_types));
  formData.append('team_ids', JSON.stringify(payload.team_ids ?? []));
  if (payload.date_from) formData.append('date_from', payload.date_from);
  if (payload.date_to) formData.append('date_to', payload.date_to);
  if (payload.period) formData.append('period', payload.period);
  formData.append('filters', JSON.stringify(payload.filters ?? {}));
  if (payload.active_ordering) {
    formData.append('active_ordering', JSON.stringify(payload.active_ordering));
  }
  charts.forEach(({ view, blob }) => {
    const field = CHART_FIELD_BY_VIEW[view];
    formData.append(field, blob, `${field}.png`);
  });
  return formData;
}

export function buildExportFilename(format: 'pdf' | 'zip'): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `analytics_page_export_${date}.${format}`;
}

export function downloadExportBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const FAILURE_REASONS: readonly GenerateFailureReason[] = [
  'file_size_exceeded',
  'page_limit_exceeded',
  'upstream_analytics_failed',
  'permission_denied',
  'png_invalid',
  'timeout',
  'throttled',
  'no_data',
  'request_failed',
  'unexpected_response_shape',
];

function isFailureReason(value: unknown): value is GenerateFailureReason {
  return typeof value === 'string' && (FAILURE_REASONS as readonly string[]).includes(value);
}

async function parseGenerateReason(data: unknown): Promise<GenerateFailureReason> {
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (isFailureReason(parsed?.reason)) return parsed.reason;
    } catch {
      // fall through
    }
  }
  return 'request_failed';
}

export async function runPageExportGenerate(
  args: BuildPreflightPayloadArgs,
  charts: ExportChartCaptureResult[]
): Promise<GenerateResult> {
  try {
    const response = await getPageExportGenerate(buildGenerateFormData(args, charts));
    const filename =
      extractFilenameFromContentDisposition(response.headers?.['content-disposition']) ??
      buildExportFilename(args.format);
    return { ok: true, blob: response.data, filename };
  } catch (error) {
    const response = (error as { response?: { data?: unknown; headers?: unknown } })?.response;
    const reason = await parseGenerateReason(response?.data);
    const retryAfter = reason === 'throttled' ? parseRetryAfter(response?.headers) : undefined;
    return { ok: false, reason, retryAfter };
  }
}
